import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Shield, Trash2, RefreshCw } from 'lucide-react';

// ✅ CORRIGIDO: adicionados todos os tipos de log do sistema
const ACAO_COR = {
  // Sessões
  SESSAO_CRIADA: 'badge-azul',
  SESSAO_STATUS: 'badge-verde',
  SESSAO_REABERTA: 'badge-amarelo',
  SESSAO_REMOVIDA: 'badge-vermelho',
  // Pautas
  PAUTA_CRIADA: 'badge-azul',
  PAUTA_EDITADA: 'badge-amarelo',
  PAUTA_REMOVIDA: 'badge-vermelho',
  PAUTA_RETIRADA: 'badge-cinza',
  // Votações
  VOTACAO_INICIADA: 'badge-verde',
  VOTACAO_ENCERRADA: 'badge-cinza',
  // Vereadores
  VEREADOR_CRIADO: 'badge-azul',
  VEREADOR_EDITADO: 'badge-amarelo',
  VEREADOR_REMOVIDO: 'badge-vermelho',
  // Projetos
  PROJETO_CRIADO: 'badge-azul',
  PROJETO_TRAMITADO: 'badge-amarelo',
  PROJETO_REMOVIDO: 'badge-vermelho',
  // Protocolos
  PROTOCOLO_CRIADO: 'badge-azul',
  PROTOCOLO_TRAMITADO: 'badge-amarelo',
  PROTOCOLO_STATUS: 'badge-cinza',
  PROTOCOLO_REMOVIDO: 'badge-vermelho',
  // Presenças
  PRESENCA_MANUAL: 'badge-verde',
  PRESENCA_REMOVIDA: 'badge-cinza',
};

const ACAO_ICONE = {
  // Sessões
  SESSAO_CRIADA: '📅',
  SESSAO_STATUS: '🔄',
  SESSAO_REABERTA: '🔓',
  SESSAO_REMOVIDA: '🗑️',
  // Pautas
  PAUTA_CRIADA: '📋',
  PAUTA_EDITADA: '✏️',
  PAUTA_REMOVIDA: '🗑️',
  PAUTA_RETIRADA: '↩️',
  // Votações
  VOTACAO_INICIADA: '🗳️',
  VOTACAO_ENCERRADA: '✅',
  // Vereadores
  VEREADOR_CRIADO: '👤',
  VEREADOR_EDITADO: '✏️',
  VEREADOR_REMOVIDO: '🗑️',
  // Projetos
  PROJETO_CRIADO: '📄',
  PROJETO_TRAMITADO: '🔀',
  PROJETO_REMOVIDO: '🗑️',
  // Protocolos
  PROTOCOLO_CRIADO: '📬',
  PROTOCOLO_TRAMITADO: '🔀',
  PROTOCOLO_STATUS: '🔄',
  PROTOCOLO_REMOVIDO: '🗑️',
  // Presenças
  PRESENCA_MANUAL: '🖊️',
  PRESENCA_REMOVIDA: '❌',
};

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('TODAS');
  const [limit, setLimit] = useState(100);

  const carregar = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/logs?limit=${limit}`);
      setLogs(r.data);
    } catch { toast.error('Erro ao carregar logs'); }
    finally { setLoading(false); }
  };

  useEffect(() => { carregar(); }, [limit]);

  const limparLogs = async () => {
    if (!window.confirm('Limpar logs com mais de 30 dias?')) return;
    try {
      const r = await axios.delete('/api/logs/limpar', { data: { dias: 30 } });
      toast.success(r.data.message);
      carregar();
    } catch { toast.error('Erro ao limpar logs'); }
  };

  const logsFiltrados = logs.filter(l => filtro === 'TODAS' || l.acao.startsWith(filtro));

  // ✅ CORRIGIDO: adicionados filtros para Projetos, Protocolos e Presenças
  const categorias = [
    { key: 'TODAS', label: 'Todos' },
    { key: 'SESSAO', label: '📅 Sessões' },
    { key: 'PAUTA', label: '📋 Pautas' },
    { key: 'VOTACAO', label: '🗳️ Votações' },
    { key: 'VEREADOR', label: '👤 Vereadores' },
    { key: 'PROJETO', label: '📄 Projetos' },
    { key: 'PROTOCOLO', label: '📬 Protocolos' },
    { key: 'PRESENCA', label: '🖊️ Presenças' },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Log de Auditoria</h1>
          <p className="page-subtitle">{logs.length} registro(s) encontrado(s)</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={carregar}>
            <RefreshCw size={15} /> Atualizar
          </button>
          <button className="btn btn-danger" onClick={limparLogs}>
            <Trash2 size={15} /> Limpar +30 dias
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        {categorias.map(c => (
          <button key={c.key} className={`btn btn-sm ${filtro === c.key ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFiltro(c.key)}>
            {c.label}
          </button>
        ))}
        <select className="form-input form-select" style={{ width: 'auto', padding: '6px 32px 6px 12px', fontSize: '0.8rem' }} value={limit} onChange={e => setLimit(Number(e.target.value))}>
          <option value={50}>Últimos 50</option>
          <option value={100}>Últimos 100</option>
          <option value={200}>Últimos 200</option>
          <option value={500}>Últimos 500</option>
        </select>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-screen" style={{ minHeight: 200 }}><div className="spinner" /></div>
        ) : logsFiltrados.length === 0 ? (
          <div className="sem-dados">
            <Shield size={40} style={{ margin: '0 auto 12px', display: 'block', opacity: 0.3 }} />
            <p>Nenhum log encontrado.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Data/Hora</th>
                  <th>Ação</th>
                  <th>Descrição</th>
                  <th>Usuário</th>
                  <th>Perfil</th>
                </tr>
              </thead>
              <tbody>
                {logsFiltrados.map(log => (
                  <tr key={log.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      {format(new Date(log.criadoEm), "dd/MM/yyyy HH:mm:ss", { locale: ptBR })}
                    </td>
                    <td>
                      <span className={`badge ${ACAO_COR[log.acao] || 'badge-cinza'}`}>
                        {ACAO_ICONE[log.acao] || '📌'} {log.acao.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>{log.descricao}</td>
                    <td style={{ fontSize: '0.85rem', fontWeight: 600 }}>{log.usuarioNome || '-'}</td>
                    <td>
                      {log.usuarioRole && (
                        <span className={`badge ${log.usuarioRole === 'admin' ? 'badge-azul' : log.usuarioRole === 'vereador' ? 'badge-verde' : 'badge-cinza'}`}>
                          {log.usuarioRole === 'admin' ? '👔 Admin' : log.usuarioRole === 'vereador' ? '🗳️ Vereador' : '🖥️ Operador'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
